/**
 * formValidation.js
 * 
 * Enterprise-grade form validation system
 * - Field validation (email, URL, phone, etc.)
 * - Input sanitization (XSS prevention, ATS-safe)
 * - Edge case handling (emoji, scripts, malformed data)
 * - Max length enforcement
 * - Whitespace handling
 */

// ══════════════════════════════════════════════════════════════════════════════
// VALIDATION REGEX PATTERNS
// ══════════════════════════════════════════════════════════════════════════════

const PATTERNS = {
  // Email: RFC 5322 simplified
  email: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
  
  // Phone: International format support
  phone: /^[\d\s()+-]+$/,
  
  // LinkedIn: Various formats
  linkedin: /^https?:\/\/(www\.)?linkedin\.com\/(in|pub|company)\/[\w-]+\/?$/i,
  
  // GitHub: Username or full URL
  github: /^(https?:\/\/)?(www\.)?github\.com\/[\w-]+\/?$/i,
  
  // Generic URL
  url: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/i,
  
  // Dangerous patterns (XSS, SQL injection)
  dangerous: /<script|javascript:|onerror=|onclick=|<iframe|eval\(|expression\(|vbscript:|data:text\/html/gi,
  
  // Emoji detection
  emoji: /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu,
};

// ══════════════════════════════════════════════════════════════════════════════
// FIELD LENGTH LIMITS (ATS-safe)
// ══════════════════════════════════════════════════════════════════════════════

export const MAX_LENGTHS = {
  name: 100,
  jobTitle: 100,
  targetHeadline: 100,
  email: 254, // RFC 5321
  phone: 20,
  location: 100,
  url: 2048,
  summary: 1000,
  company: 100,
  role: 100,
  duration: 50,
  achievement: 500,
  skill: 50,
  competency: 100,
  degree: 150,
  university: 150,
  certification: 150,
  organization: 150,
  projectTitle: 150,
  projectDescription: 500,
  technologies: 200,
};

// ══════════════════════════════════════════════════════════════════════════════
// SANITIZATION FUNCTIONS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Remove dangerous HTML/script content
 */
export const sanitizeHTML = (input) => {
  if (typeof input !== 'string') return '';
  
  // Remove script tags and dangerous attributes
  let clean = input.replace(PATTERNS.dangerous, '');
  
  // Remove HTML tags but keep content
  clean = clean.replace(/<[^>]*>/g, '');
  
  // Decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = clean;
  clean = textarea.value;
  
  return clean;
};

/**
 * Sanitize for ATS compatibility
 * - Remove emojis (ATS parsers can't handle them)
 * - Remove special characters that break parsing
 * - Normalize whitespace
 */
export const sanitizeForATS = (input) => {
  if (typeof input !== 'string') return '';
  
  let clean = input;
  
  // Remove emojis
  clean = clean.replace(PATTERNS.emoji, '');
  
  // Remove zero-width characters
  clean = clean.replace(/[\u200B-\u200D\uFEFF]/g, '');
  
  // Normalize whitespace
  clean = clean.replace(/\s+/g, ' ').trim();
  
  // Remove control characters except newlines and tabs
  clean = clean.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  
  return clean;
};

/**
 * Trim and normalize whitespace
 */
export const normalizeWhitespace = (input) => {
  if (typeof input !== 'string') return '';
  return input.replace(/\s+/g, ' ').trim();
};

/**
 * Sanitize URL
 */
export const sanitizeURL = (input) => {
  if (typeof input !== 'string') return '';
  
  let clean = input.trim();
  
  // Remove dangerous protocols
  if (clean.match(/^(javascript|data|vbscript):/i)) {
    return '';
  }
  
  // Ensure http/https
  if (clean && !clean.match(/^https?:\/\//i)) {
    clean = 'https://' + clean;
  }
  
  return clean;
};

/**
 * Sanitize phone number
 */
export const sanitizePhone = (input) => {
  if (typeof input !== 'string') return '';
  
  // Keep only digits, spaces, parentheses, plus, and hyphens
  return input.replace(/[^\d\s()+-]/g, '').trim();
};

/**
 * Full sanitization pipeline
 */
export const sanitizeInput = (input, type = 'text') => {
  if (typeof input !== 'string') return '';
  
  // Step 1: Remove dangerous content
  let clean = sanitizeHTML(input);
  
  // Step 2: Type-specific sanitization
  switch (type) {
    case 'url':
    case 'linkedin':
    case 'github':
      clean = sanitizeURL(clean);
      break;
    case 'phone':
      clean = sanitizePhone(clean);
      break;
    case 'email':
      clean = clean.toLowerCase().trim();
      break;
    default:
      clean = sanitizeForATS(clean);
  }
  
  return clean;
};

// ══════════════════════════════════════════════════════════════════════════════
// VALIDATION FUNCTIONS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Validate email
 */
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return { valid: false, error: 'Email is required' };
  }
  
  const clean = email.trim().toLowerCase();
  
  if (clean.length === 0) {
    return { valid: false, error: 'Email is required' };
  }
  
  if (clean.length > MAX_LENGTHS.email) {
    return { valid: false, error: 'Email is too long' };
  }
  
  if (!PATTERNS.email.test(clean)) {
    return { valid: false, error: 'Enter a valid email address' };
  }
  
  // Check for common typos
  const commonDomains = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com'];
  const domain = clean.split('@')[1];
  const typos = {
    'gmial.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'yahooo.com': 'yahoo.com',
    'outlok.com': 'outlook.com',
  };
  
  if (typos[domain]) {
    return { 
      valid: false, 
      error: `Did you mean ${clean.replace(domain, typos[domain])}?`,
      suggestion: clean.replace(domain, typos[domain])
    };
  }
  
  return { valid: true, value: clean };
};

/**
 * Validate phone number
 */
export const validatePhone = (phone) => {
  if (!phone || typeof phone !== 'string') {
    return { valid: true, value: '' }; // Optional field
  }
  
  const clean = sanitizePhone(phone);
  
  if (clean.length === 0) {
    return { valid: true, value: '' };
  }
  
  if (clean.length > MAX_LENGTHS.phone) {
    return { valid: false, error: 'Phone number is too long' };
  }
  
  if (!PATTERNS.phone.test(clean)) {
    return { valid: false, error: 'Enter a valid phone number' };
  }
  
  // Check minimum length (at least 10 digits)
  const digits = clean.replace(/\D/g, '');
  if (digits.length < 10) {
    return { valid: false, error: 'Phone number must have at least 10 digits' };
  }
  
  return { valid: true, value: clean };
};

/**
 * Validate LinkedIn URL
 */
export const validateLinkedIn = (url) => {
  if (!url || typeof url !== 'string') {
    return { valid: true, value: '' }; // Optional field
  }
  
  const clean = sanitizeURL(url);
  
  if (clean.length === 0) {
    return { valid: true, value: '' };
  }
  
  if (clean.length > MAX_LENGTHS.url) {
    return { valid: false, error: 'URL is too long' };
  }
  
  if (!PATTERNS.linkedin.test(clean)) {
    return { 
      valid: false, 
      error: 'Enter a valid LinkedIn URL (e.g., linkedin.com/in/username)' 
    };
  }
  
  return { valid: true, value: clean };
};

/**
 * Validate GitHub URL
 */
export const validateGitHub = (url) => {
  if (!url || typeof url !== 'string') {
    return { valid: true, value: '' }; // Optional field
  }
  
  const clean = sanitizeURL(url);
  
  if (clean.length === 0) {
    return { valid: true, value: '' };
  }
  
  if (clean.length > MAX_LENGTHS.url) {
    return { valid: false, error: 'URL is too long' };
  }
  
  if (!PATTERNS.github.test(clean)) {
    return { 
      valid: false, 
      error: 'Enter a valid GitHub URL (e.g., github.com/username)' 
    };
  }
  
  return { valid: true, value: clean };
};

/**
 * Validate generic URL
 */
export const validateURL = (url, fieldName = 'URL') => {
  if (!url || typeof url !== 'string') {
    return { valid: true, value: '' }; // Optional field
  }
  
  const clean = sanitizeURL(url);
  
  if (clean.length === 0) {
    return { valid: true, value: '' };
  }
  
  if (clean.length > MAX_LENGTHS.url) {
    return { valid: false, error: `${fieldName} is too long` };
  }
  
  if (!PATTERNS.url.test(clean)) {
    return { 
      valid: false, 
      error: `Enter a valid ${fieldName} (e.g., https://example.com)` 
    };
  }
  
  return { valid: true, value: clean };
};

/**
 * Validate required text field
 */
export const validateRequired = (value, fieldName, maxLength = null) => {
  if (!value || typeof value !== 'string') {
    return { valid: false, error: `${fieldName} is required` };
  }
  
  const clean = normalizeWhitespace(value);
  
  if (clean.length === 0) {
    return { valid: false, error: `${fieldName} is required` };
  }
  
  if (maxLength && clean.length > maxLength) {
    return { 
      valid: false, 
      error: `${fieldName} must be less than ${maxLength} characters` 
    };
  }
  
  return { valid: true, value: clean };
};

/**
 * Validate optional text field
 */
export const validateOptional = (value, fieldName, maxLength = null) => {
  if (!value || typeof value !== 'string') {
    return { valid: true, value: '' };
  }
  
  const clean = normalizeWhitespace(value);
  
  if (clean.length === 0) {
    return { valid: true, value: '' };
  }
  
  if (maxLength && clean.length > maxLength) {
    return { 
      valid: false, 
      error: `${fieldName} must be less than ${maxLength} characters` 
    };
  }
  
  return { valid: true, value: clean };
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPREHENSIVE FORM VALIDATION
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Validate personal information step
 */
export const validatePersonalInfo = (data) => {
  const errors = {};
  
  // Required: Name
  const nameResult = validateRequired(data.personal?.name, 'Full name', MAX_LENGTHS.name);
  if (!nameResult.valid) errors.name = nameResult.error;
  
  // Required: Email
  const emailResult = validateEmail(data.personal?.email);
  if (!emailResult.valid) errors.email = emailResult.error;
  
  // Optional: Job Title
  const jobTitleResult = validateOptional(data.personal?.jobTitle, 'Job title', MAX_LENGTHS.jobTitle);
  if (!jobTitleResult.valid) errors.jobTitle = jobTitleResult.error;
  
  // Optional: Phone
  const phoneResult = validatePhone(data.personal?.phone);
  if (!phoneResult.valid) errors.phone = phoneResult.error;
  
  // Optional: Location
  const locationResult = validateOptional(data.personal?.location, 'Location', MAX_LENGTHS.location);
  if (!locationResult.valid) errors.location = locationResult.error;
  
  // Optional: LinkedIn
  const linkedinResult = validateLinkedIn(data.personal?.linkedin);
  if (!linkedinResult.valid) errors.linkedin = linkedinResult.error;
  
  // Optional: GitHub
  const githubResult = validateGitHub(data.personal?.github);
  if (!githubResult.valid) errors.github = githubResult.error;
  
  // Optional: Portfolio
  const portfolioResult = validateURL(data.personal?.portfolio, 'Portfolio URL');
  if (!portfolioResult.valid) errors.portfolio = portfolioResult.error;
  
  return errors;
};

/**
 * Validate summary step
 */
export const validateSummary = (data) => {
  const errors = {};
  
  const summaryResult = validateOptional(data.summary, 'Professional summary', MAX_LENGTHS.summary);
  if (!summaryResult.valid) errors.summary = summaryResult.error;
  
  return errors;
};

/**
 * Validate experience entry
 */
export const validateExperience = (exp) => {
  const errors = {};
  
  const companyResult = validateOptional(exp.company, 'Company', MAX_LENGTHS.company);
  if (!companyResult.valid) errors.company = companyResult.error;
  
  const roleResult = validateOptional(exp.role, 'Role', MAX_LENGTHS.role);
  if (!roleResult.valid) errors.role = roleResult.error;
  
  const durationResult = validateOptional(exp.duration, 'Duration', MAX_LENGTHS.duration);
  if (!durationResult.valid) errors.duration = durationResult.error;
  
  const locationResult = validateOptional(exp.location, 'Location', MAX_LENGTHS.location);
  if (!locationResult.valid) errors.location = locationResult.error;
  
  return errors;
};

// ══════════════════════════════════════════════════════════════════════════════
// EDGE CASE DETECTION
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Detect potential issues in input
 */
export const detectIssues = (input) => {
  const issues = [];
  
  if (typeof input !== 'string') return issues;
  
  // Check for emojis
  if (PATTERNS.emoji.test(input)) {
    issues.push({
      type: 'emoji',
      severity: 'warning',
      message: 'Emojis may not be ATS-compatible. Consider removing them.',
    });
  }
  
  // Check for excessive length
  if (input.length > 1000) {
    issues.push({
      type: 'length',
      severity: 'warning',
      message: 'This field is very long. Consider shortening for better readability.',
    });
  }
  
  // Check for dangerous content
  if (PATTERNS.dangerous.test(input)) {
    issues.push({
      type: 'security',
      severity: 'error',
      message: 'This input contains potentially dangerous content.',
    });
  }
  
  // Check for excessive whitespace
  if (input.match(/\s{5,}/)) {
    issues.push({
      type: 'whitespace',
      severity: 'info',
      message: 'Excessive whitespace detected. It will be normalized.',
    });
  }
  
  return issues;
};

/**
 * Check if input is safe for ATS
 */
export const isATSSafe = (input) => {
  if (typeof input !== 'string') return true;
  
  // Check for emojis
  if (PATTERNS.emoji.test(input)) return false;
  
  // Check for dangerous content
  if (PATTERNS.dangerous.test(input)) return false;
  
  // Check for excessive special characters
  const specialChars = input.match(/[^a-zA-Z0-9\s.,;:()\-+@#$%&*]/g);
  if (specialChars && specialChars.length > input.length * 0.1) return false;
  
  return true;
};

export default {
  validateEmail,
  validatePhone,
  validateLinkedIn,
  validateGitHub,
  validateURL,
  validateRequired,
  validateOptional,
  validatePersonalInfo,
  validateSummary,
  validateExperience,
  sanitizeInput,
  sanitizeForATS,
  detectIssues,
  isATSSafe,
  MAX_LENGTHS,
};
